import { useMemo } from 'react'

import { AgChartOptions } from 'ag-charts-community'
import { AgCharts } from 'ag-charts-react'
import { format, parseISO, set } from 'date-fns'

import { getDatesInMinuteRange } from '@fortem-monorepo/js-utilities'
import { Logs } from '@fortem-monorepo/web-sdk'

export interface DailyTasksChartProps {
  date: Date
  tasks: Array<Logs.ScheduledTask>
}

export const DailyTasksChart: React.FC<DailyTasksChartProps> = ({ date, tasks }) => {
  // Array of dates within a range, given a selected date
  const timePeriodIntervals = useMemo(
    () =>
      getDatesInMinuteRange(
        set(date, {
          hours: 0,
          minutes: 0,
          seconds: 0,
        }),
        set(date, { hours: 23, minutes: 23, seconds: 59 }),
        30,
      ),
    [date],
  )

  // Returns chartData and a list of visible task names
  const [chartData, taskNamesWithTimePeriods] = useMemo(() => {
    const withTimePeriods = (tasks ?? []).map((task) => {
      const parsedDate = parseISO(task.upload_datetime_utc)

      const timePeriodInterval = timePeriodIntervals.find(
        (period) => parsedDate >= period.start && parsedDate < period.end,
      )

      return {
        ...task,
        ...(timePeriodInterval && {
          time_period: `${format(timePeriodInterval?.start, 'HH:mm')}`,
        }),
      }
    })

    const grouped = withTimePeriods.reduce(
      (prev, curr) => {
        // TOOD: revist this undefined type guard
        if (curr.time_period === undefined) return prev

        const _new = { ...prev }

        if (!_new[curr.time_period]) {
          _new[curr.time_period] = {}
        }

        if (!_new[curr.time_period][curr.description]) {
          _new[curr.time_period][curr.description] = 0
        }

        _new[curr.time_period][curr.description]++

        return _new
      },
      {} as {
        [key: string]: {
          [key: string]: number
        }
      },
    )

    return [
      Object.entries(grouped)
        .map(([time_period, numbers]) => ({
          time_period,
          ...numbers,
        }))
        .sort(
          (a, b) => Number(a.time_period.replace(':', '')) - Number(b.time_period.replace(':', '')),
        ),
      withTimePeriods.reduce((prev, curr) => {
        const _new = [...prev]
        if (!_new.includes(curr.description) && curr.time_period) {
          _new.push(curr.description)
        }
        return _new
      }, [] as Array<string>),
    ]
  }, [tasks, date])

  const chartOptions: AgChartOptions = {
    data: chartData,
    legend: {
      enabled: false,
    },
    animation: {
      enabled: true,
    },
    background: {
      visible: false,
    },
    series: taskNamesWithTimePeriods.map((taskName) => {
      return {
        type: 'bar',
        xKey: 'time_period',
        yKey: taskName,
        yName: taskName,
        // stackGroup: "df",
        stacked: true,
      }
    }),
  }

  return <AgCharts options={chartOptions} className={'w-full h-full overflow-hidden rounded-md'} />
}
